import React, { FC, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import $ from "jquery";
import { makeEventsUnique } from "./utils/helpers";

declare global {
  interface Window {
    jQuery: typeof $;
    Webflow: any;
    $: typeof $;
  }
}

type LoadWebflow = (pageId: string) => void | undefined;
type GetEvents<PT extends object = {}> = (
  pageId: string,
  passThrough: PT
) => any;

type LoadModule<PT extends object = {}> =
  | string
  | {
      module: string;
      onLoad?: LoadWebflow;
      getEvents?: GetEvents<PT>;
    };

export const getCurrentWebFlowPageId = (): string | undefined => {
  if (typeof document === "undefined") return undefined;

  const html = document?.getElementsByTagName("html")[0];
  return html?.getAttribute("data-wf-page") ?? undefined;
};

export const triggerWebflowAnimations = <PT extends object>(
  passThrough: PT,
  modules?: LoadModule<PT>[]
): void => {
  if (typeof document === "undefined") return;
  const webflowPageId = getCurrentWebFlowPageId();
  if (!webflowPageId) return;

  (modules ?? []).forEach((module) => {
    if (typeof module !== "string") {
      if (module.module === "ix2") {
        const ix2Module = window?.Webflow?.require("ix2");
        const ixData = ix2Module.store?.getState().ixData;
        const mediaQueries = {
          site: {
            mediaQueries: [
              { key: "main", min: 992, max: 10000 },
              { key: "medium", min: 768, max: 991 },
              { key: "small", min: 480, max: 767 },
              { key: "tiny", min: 0, max: 479 },
            ],
          },
        };
        const existingEvents = ixData.events;
        const existingActions = ixData.actionLists;
        const ix2Events = makeEventsUnique(
          module?.getEvents?.(webflowPageId, passThrough) ?? {}
        );

        ix2Module?.init({
          events: { ...existingEvents, ...ix2Events.events },
          actionLists: { ...existingActions, ...ix2Events.actionLists },
          ...mediaQueries,
        });
      } else {
        module?.onLoad?.(webflowPageId);
      }
    } else {
      const requiredModule = window?.Webflow?.require(module)?.redraw?.();
      requiredModule?.redraw?.();
    }
  });

  window?.Webflow?.ready();
  document?.dispatchEvent(new Event("readystatechange"));
};

/**
 * This function can be called to re(load) webflow animations that is located
 * within a webflow.js script file.
 *
 * It'll enforce the current set `wf-page` identifier as html atrribute,
 * or generates a new one.
 *
 * This can be usually called on a componentDidMount lifecycle to retrigger/reload
 * animations within the calling component.
 */
const prepareWebflowAnimations = (): void => {
  if (typeof document === "undefined") return;

  window?.Webflow?.ready();

  // Let's get the current wf-page id, or generate a new one and set as html atr
  const thisComponent = document?.getElementsByTagName("html")[0];
  const pageId = thisComponent?.getAttribute("data-wf-page") ?? uuidv4();
  // Use the same pageId as param for the webflow.load, to load the animations
  thisComponent?.setAttribute("data-wf-page", pageId);
};

// passThrough function is passed as additional prop on top of the original component props
// when wrapping a component within the `webflowHOC`.
// Within the original component, this method can be called to pass props of a certain type to the `getEvents` handler
type PassThroughProps<PT> = {
  passThrough?: (props: PT) => void;
};
export type WebflowComponentProps<OriginalProps, PT> = OriginalProps &
  PassThroughProps<PT>;

type OriginalProps<P> = Omit<
  WebflowComponentProps<P, any>,
  keyof PassThroughProps<any>
>;

export const webflowHOC = <
  P extends WebflowComponentProps<Original, PassThrough>,
  PassThrough extends object,
  Original = OriginalProps<P>
>(
  WrappedComponent: FC<P>,
  modules: LoadModule<PassThrough>[] = []
): FC<Original> => {
  const WrapperComponent: FC<Original> = (originalProps: any) => {
    if (typeof window === "undefined")
      return <WrappedComponent {...originalProps} />;

    const [jQueryLoaded, setjQueryLoaded] = useState(
      window.jQuery !== undefined
    );
    const [webflowLoaded, setWebflowLoaded] = useState<boolean>();

    const [passThrough, setPassThrough] = useState<PassThrough | {}>({});
    const props: P = {
      ...originalProps,
      passThrough: (passThroughProps: PassThrough) => {
        setPassThrough(passThroughProps);
      },
    };

    useEffect(() => {
      // Reload the webflow animations whenever the passThrough props we're updated from the child component
      triggerWebflowAnimations(passThrough as any, modules);
    }, [passThrough]);

    const loadWebflow = () => {
      if (typeof window.jQuery !== undefined) {
        import("./webflow").then(() => {
          prepareWebflowAnimations();
          setWebflowLoaded(true);
        });
      }
    };

    // We want our webflow animations to be retriggered AFTER the render of the child.
    // This is because we want the useEffect of the child component to take effect first (this might change the onLoad props within the modules)
    useEffect(() => {
      triggerWebflowAnimations(passThrough as PassThrough, modules);
    }, [props, webflowLoaded]);

    useEffect(() => {
      loadWebflow();
    }, [jQueryLoaded]);

    useEffect(() => {
      if (!jQueryLoaded) {
        window.jQuery = $;
        window.$ = $;
        setjQueryLoaded(true);
      }
    }, []);

    return <WrappedComponent {...props} />;
  };
  return WrapperComponent;
};
